import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { FormatService } from 'src/core/services/format.service';
import { DialogService } from 'src/core/services/ui/dialog.service';
import { CurrencyExchangeRateListView } from './currency-exchange-rate-list.view';
import { CurrencyExchangeRateListDataService } from './currency-exchange-rate-list.data';

interface CurrencyExchangeRateListControllerProps {
	type: number;
	title: string;
	setBusy(isBusy: boolean);
	defaultCurrency: CurrencySummary;
	currencies: CurrencySummary[];
	contextService: any;
}

@observer
export class CurrencyExchangeRateListController extends React.Component<CurrencyExchangeRateListControllerProps, any> {
	@observable
	protected dataService: CurrencyExchangeRateListDataService;

	@resolve
	protected dialogService: DialogService;

	@resolve
	protected formatService: FormatService;

	@resolve
	protected toast: ToastNotifications;

	@observable
	protected listToGroup: CurrencyExchangeRateItem[];

	@observable
	currencies: any[] = [];

	private date: Date;

	constructor(props) {
		super(props);
	}

	@autobind
	protected loadData(defaultCurrency: number) {
		this.props.setBusy(true);
		this.dataService.addFilter('mode', 'eq', this.props.type);
		this.dataService.addSorting('bookingDateFrom', 'desc', true);
		this.dataService.pageSize = 20;

		this.dataService
			.load(function (entitySet) {
				return entitySet.filter(`it.fromCurrencyId == ${JSON.stringify(defaultCurrency)} || it.toCurrencyId == ${JSON.stringify(defaultCurrency)}`);
			})
			.then((items) => {
				this.listToGroup = items;
				this.props.setBusy(false);
			})
			.catch((error) => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
	}

	@autobind
	private groupByCurrencies(items: CurrencyExchangeRateItem[]): GroupedRates[] {
		const rows: GroupedRates[] = [];
		if (items) {
			for (const rate of items) {
				if (rate.fromCurrencyId != this.props.defaultCurrency.id && rate.toCurrencyId != this.props.defaultCurrency.id) continue;
				const fromCurrency = this.props.currencies.find(c => c.id == rate.fromCurrencyId);
				const toCurrency = this.props.currencies.find(c => c.id == rate.toCurrencyId);
				const isFrom = fromCurrency.id == this.props.defaultCurrency.id;
				let obj: GroupedRates = rows.find(e => e.displayName == (isFrom ? toCurrency : fromCurrency).iso3Name
					&& rate.bookingDateFromText == e.bookingDateFromText && rate.bookingDateToText == e.bookingDateToText
					&& rate.serviceDateFromText == e.serviceDateFromText && rate.serviceDateToText == e.serviceDateToText);
				if (obj == null) {
					obj = {
						isNew: rate.entity.entityState == 20,
						displayName: (isFrom ? toCurrency : fromCurrency).iso3Name,
						fromCurrency: isFrom ? rate.id : null,
						toCurrency: isFrom ? null : rate.id,
						bookingDateFromText: rate.bookingDateFromText,
						bookingDateToText: rate.bookingDateToText,
						serviceDateFromText: rate.serviceDateFromText,
						serviceDateToText: rate.serviceDateToText
					};
					rows.push(obj);
				}
				if (isFrom) {
					obj.fromCurrency = rate.id;
				} else {
					obj.toCurrency = rate.id;
				}
				obj[isFrom ? 'fromCurrencyRate' : 'toCurrencyRate'] = rate.rate;
			}
		}

		return rows;
	}

	@autobind
	private createNewDates() {
		this.date = new Date();
		return Promise
			.all([
				this.dataService.create(this.createFrom),
				this.dataService.create(this.createTo)
			])
			.then((items) => {
				this.props.setBusy(false);
				return items;
			})
			.catch((error) => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
	}

	@autobind
	protected createFrom() {
		const rate = this.dataService.add(this.date, this.props.type);
		rate.fromCurrencyId = this.props.defaultCurrency.id;
		rate.toCurrencyId = this.props.currencies.find(e => e.id != this.props.defaultCurrency.id).id;
		return rate;
	}

	@autobind
	protected createTo() {
		const rate = this.dataService.add(this.date, this.props.type);
		rate.toCurrencyId = this.props.defaultCurrency.id;
		rate.fromCurrencyId = this.props.currencies.find(e => e.id != this.props.defaultCurrency.id).id;
		return rate;
	}

	componentDidMount() {
		this.dataService = CurrencyExchangeRateListDataService.make(this.props.contextService, this.formatService);
		this.loadData(this.props.defaultCurrency.id);
	}

	componentWillReceiveProps?(nextProps: Readonly<CurrencyExchangeRateListControllerProps>, nextContext: any) {
		if (this.props.defaultCurrency.id != nextProps.defaultCurrency.id) {
			this.loadData(nextProps.defaultCurrency.id);
		}
	}

	render() {
		if (this.dataService == null || this.dataService.items == null) {
			return null;
		}

		return (
			<CurrencyExchangeRateListView
				title={this.props.title}
				model={this.dataService}
				setBusy={this.props.setBusy}
				currencies={this.props.currencies}
				createNewDates={this.createNewDates}
				defaultCurrency={this.props.defaultCurrency}
				listToGroup={this.listToGroup}
				groupByCurrencies={this.groupByCurrencies}
				currenciesGrouped={this.currencies}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/currency-exchange-rates/list/currency-exchange-rate-list.controller.tsx